/**
 * What the camera and the falling bits do — kept apart from the rules (engine)
 * and the drawing (renderer) so it can be tested.
 *
 * World units are studs; y points up from the ground (y = 0).
 */
import type { Floor, StackerView } from "./engine";

/** Height of one floor, in studs (the brick's front face is 1.1 studs tall). */
export const FLOOR_HEIGHT = 1.1;
/** Gap between the top of the tower and the sliding brick. */
export const HOVER = 2.8;
/** Studs visible across the screen at zoom 1 (the 16-stud field plus margins). */
export const VIEW_STUDS = 19;
/** How much ground stays visible under the tower. */
const GROUND_MARGIN = 1.6;

export interface Camera {
  /** World y at the bottom edge of the screen. */
  bottom: number;
  /** 1 = normal; 2 = everything half the size. */
  zoom: number;
}

/** World y of the top edge of floor `index`'s front face. */
export function floorTop(index: number): number {
  return (index + 1) * FLOOR_HEIGHT;
}

/** World y of the top edge of the sliding brick's front face. */
export function movingTop(floorsCount: number): number {
  return floorTop(floorsCount - 1) + HOVER + FLOOR_HEIGHT;
}

/**
 * Where the camera wants to be. Playing: the sliding brick sits about a quarter
 * of the way down the screen. Game over: the whole tower fits.
 * `aspect` is the screen's height divided by its width.
 */
export function cameraTarget(view: StackerView, aspect: number): Camera {
  const visible = VIEW_STUDS * aspect; // studs of height on screen at zoom 1
  const n = view.floors.length;
  if (view.status === "over") {
    const towerTop = floorTop(n - 1) + 2.5;
    const zoom = Math.max(1, (towerTop + GROUND_MARGIN) / visible);
    return { bottom: -GROUND_MARGIN, zoom };
  }
  const want = movingTop(n) + 0.5 - visible * 0.75;
  return { bottom: Math.max(-GROUND_MARGIN, want), zoom: 1 };
}

/** Eases the camera toward the target; `instant` (reduced motion) jumps straight there. */
export function stepCamera(cam: Camera, target: Camera, dt: number, instant = false): Camera {
  if (instant) return { ...target };
  const k = 1 - Math.exp(-6 * dt);
  const next = {
    bottom: cam.bottom + (target.bottom - cam.bottom) * k,
    zoom: cam.zoom + (target.zoom - cam.zoom) * k,
  };
  if (Math.abs(next.bottom - target.bottom) < 0.001 && Math.abs(next.zoom - target.zoom) < 0.0005) return { ...target };
  return next;
}

export interface Debris extends Floor {
  /** World y of the top of its front face. */
  y: number;
  vx: number;
  vy: number;
  /** Radians. */
  rot: number;
  spin: number;
  color: string;
}

const GRAVITY = 40;

/** A piece that broke off at floor `index`, tumbling away from the tower on its own side. */
export function spawnDebris(piece: Floor, index: number, color: string, side: 1 | -1, y = floorTop(index)): Debris {
  return { ...piece, y, vx: side * 2.2, vy: 1.5, rot: 0, spin: side * 2.6, color };
}

/** Moves the falling pieces; drops the ones that have fallen below `floorY`. */
export function stepDebris(list: Debris[], dt: number, floorY: number): Debris[] {
  return list
    .map((d) => ({
      ...d,
      vy: d.vy - GRAVITY * dt,
      y: d.y + (d.vy - (GRAVITY * dt) / 2) * dt,
      x: d.x + d.vx * dt,
      rot: d.rot + d.spin * dt,
    }))
    .filter((d) => d.y > floorY);
}
